import { useMemo } from 'react';
import type { CSSProperties } from 'react';

import { env } from '@/constants/env';
import { brandTextHsl, isColorDark } from '../utils/brand-color';
import { hexToHsl } from '../utils/color';

type TenantThemeSource = {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  colorSchema?: string | null;
  logo?: string | null;
  logoId?: string | null;
  name?: string | null;
};

type UseTenantThemeResult = {
  themeVars: CSSProperties;
  tenantPrimaryColor?: string;
  tenantSecondaryColor?: string;
  tenantAccentColor?: string;
  logoUrl: string | null;
};

export const useTenantTheme = (source?: TenantThemeSource): UseTenantThemeResult => {
  const tenantPrimaryColor = source?.primaryColor ?? undefined;
  const tenantSecondaryColor = source?.secondaryColor ?? undefined;
  const tenantAccentColor = source?.accentColor ?? undefined;

  const logoId = source?.logoId ?? source?.logo ?? null;
  const logoUrl = logoId ? `${env.API_URL}/images/${logoId}` : null;

  const themeVars = useMemo<CSSProperties>(() => {
    if (!tenantPrimaryColor && !tenantSecondaryColor && !tenantAccentColor) {
      return {};
    }

    const vars: Record<string, string> = {};

    const primaryHsl = tenantPrimaryColor ? hexToHsl(tenantPrimaryColor) : null;
    const secondaryHsl = tenantSecondaryColor ? hexToHsl(tenantSecondaryColor) : null;
    const accentHsl = tenantAccentColor ? hexToHsl(tenantAccentColor) : null;

    if (tenantPrimaryColor) {
      (vars as Record<string, string>)['--tenant-primary'] = tenantPrimaryColor;
    }

    if (tenantSecondaryColor) {
      (vars as Record<string, string>)['--tenant-secondary'] = tenantSecondaryColor;
    }

    if (tenantAccentColor) {
      (vars as Record<string, string>)['--tenant-accent'] = tenantAccentColor;
    }

    if (primaryHsl) {
      (vars as Record<string, string>)['--primary-color-light'] = primaryHsl;
      (vars as Record<string, string>)['--primary-color'] = primaryHsl;
    }

    // Used only inside the `.new-ui` shell scope: brand shade for text on white and the text color
    // that sits on top of a brand colored button
    if (tenantPrimaryColor && primaryHsl) {
      vars['--brand-text'] = brandTextHsl(tenantPrimaryColor) ?? primaryHsl;
      vars['--brand-foreground'] = isColorDark(tenantPrimaryColor) ? '0 0% 100%' : '215 25% 27%';
    }

    if (secondaryHsl) {
      (vars as Record<string, string>)['--text-color-primary'] = secondaryHsl;
      (vars as Record<string, string>)['--primary-color-dark'] = secondaryHsl;
      (vars as Record<string, string>)['--primary-color-darker'] = secondaryHsl;
    }

    if (accentHsl) {
      vars['--background-color-lighter'] = accentHsl;
    }

    return vars;
  }, [tenantPrimaryColor, tenantSecondaryColor, tenantAccentColor]);

  return {
    themeVars,
    tenantPrimaryColor,
    tenantSecondaryColor,
    tenantAccentColor,
    logoUrl,
  };
};

export default useTenantTheme;
